import type { FunctionComponent } from "preact";
import { useEffect, useId, useMemo, useState } from "preact/hooks";

import { fetchDirectory } from "./api.js";
import { dateParts } from "./format.js";
import { Lightbox, resolveColor, SOCIAL_ICON_VIEWBOX, SOCIAL_ICONS } from "./ListView.js";
import { useLocale, useT, type Translator } from "./messages.js";
import { stripRichText } from "./richtext.js";
import { isSubRegion, type DirectoryNextAction, type DirectoryOrg, type EmbedConfig, type RegionLevel } from "./types.js";

// Initiative profile pages always live on the public site, even when the embed
// talks to a local/staging API — mirrors the org links in the Lightbox.
const PROFILE_BASE = "https://cleanups.berlin";

const SOCIAL_FIELDS: { key: keyof DirectoryOrg; label: string }[] = [
  { key: "website_url", label: "Website" },
  { key: "whatsapp_url", label: "WhatsApp" },
  { key: "signal_url", label: "Signal" },
  { key: "telegram_url", label: "Telegram" },
  { key: "instagram_url", label: "Instagram" },
  { key: "nebenan_url", label: "nebenan.de" },
  { key: "facebook_url", label: "Facebook" },
  { key: "linktree_url", label: "Linktree" },
];

function regionLabel(org: DirectoryOrg, t: Translator): string {
  const hr = org.home_region;
  if (!hr) return t("cityWide");
  if (isSubRegion(hr.level) && hr.parent_name) {
    // Chevron reads as hierarchy "district contains subdistrict" (#158).
    return `${hr.parent_name} › ${hr.name}`;
  }
  return hr.name;
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// ─── Filter option builders (client-side, mirrors the /initiativen browser) ──
interface RegionOption { slug: string; name: string; level?: RegionLevel }

function buildDistrictOptions(orgs: DirectoryOrg[]): RegionOption[] {
  const seen = new Map<string, string>();
  for (const org of orgs) {
    const hr = org.home_region;
    if (!hr) continue;
    if (hr.level === "district") {
      if (!seen.has(hr.slug)) seen.set(hr.slug, hr.name);
    } else if (hr.parent_slug && hr.parent_name) {
      if (!seen.has(hr.parent_slug)) seen.set(hr.parent_slug, hr.parent_name);
    }
  }
  return [...seen.entries()]
    .map(([slug, name]) => ({ slug, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function buildSubdistrictOptions(orgs: DirectoryOrg[], districtSlug: string): RegionOption[] {
  const seen = new Map<string, RegionOption>();
  for (const org of orgs) {
    const hr = org.home_region;
    if (hr && isSubRegion(hr.level) && hr.parent_slug === districtSlug) {
      seen.set(hr.slug, { slug: hr.slug, name: hr.name, level: hr.level });
    }
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// ─── DirectoryView ───────────────────────────────────────────────────────────
type LoadState =
  | { status: "loading" }
  | { status: "ready"; orgs: DirectoryOrg[] }
  | { status: "error" };

export const DirectoryView: FunctionComponent<{ config: EmbedConfig }> = ({ config }) => {
  const t = useT();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [district, setDistrict] = useState<string | undefined>(undefined);
  const [subdistrict, setSubdistrict] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<{ id: string; ids: string[] } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDirectory(config.apiBase)
      .then((orgs) => { if (!cancelled) setState({ status: "ready", orgs }); })
      .catch(() => { if (!cancelled) setState({ status: "error" }); });
    return () => { cancelled = true; };
  }, [config.apiBase]);

  const orgs = state.status === "ready" ? state.orgs : [];
  const districts = useMemo(() => buildDistrictOptions(orgs), [orgs]);
  const subdistricts = useMemo(
    () => (district ? buildSubdistrictOptions(orgs, district) : []),
    [orgs, district],
  );

  const filtered = useMemo(() => {
    if (!district) return orgs;
    return orgs.filter((org) => {
      const hr = org.home_region;
      if (!hr) return false;
      if (subdistrict) return hr.slug === subdistrict;
      if (hr.level === "district") return hr.slug === district;
      return hr.parent_slug === district;
    });
  }, [orgs, district, subdistrict]);

  function selectDistrict(slug: string | undefined) {
    setDistrict(slug);
    setSubdistrict(undefined);
  }

  const gridStyle = config.columns
    ? { gridTemplateColumns: `repeat(${config.columns}, 1fr)` }
    : undefined;

  return (
    <>
      {districts.length > 0 && (
        <RegionFilter
          options={districts}
          selected={district}
          onSelect={selectDistrict}
          label={t("filterByDistrict")}
        />
      )}
      {subdistricts.length > 0 && (
        <RegionFilter
          options={subdistricts}
          selected={subdistrict}
          onSelect={(s) => setSubdistrict(s)}
          label={subdistricts[0]?.level === "municipality"
            ? t("filterByMunicipality")
            : t("filterBySubdistrict")}
        />
      )}

      {state.status === "loading" && <div class="cb-empty">{t("loading")}</div>}
      {state.status === "error" && <div class="cb-error">{t("loadError")}</div>}
      {state.status === "ready" && filtered.length === 0 && (
        <div class="cb-empty">{t("dirEmpty")}</div>
      )}
      {state.status === "ready" && filtered.length > 0 && (
        <ul class="cb-dir-grid" style={gridStyle}>
          {filtered.map((org) => (
            <OrgTile
              key={org.id}
              org={org}
              onOpen={(id) => setSelected({ id, ids: org.next_actions.map((a) => a.id) })}
            />
          ))}
        </ul>
      )}

      {selected && (
        <Lightbox
          id={selected.id}
          navIds={selected.ids}
          onNavigate={(id) => setSelected({ id, ids: selected.ids })}
          config={config}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
};

// ─── OrgTile ─────────────────────────────────────────────────────────────────
const OrgTile: FunctionComponent<{
  org: DirectoryOrg;
  onOpen: (id: string) => void;
}> = ({ org, onOpen }) => {
  const t = useT();
  const color = resolveColor(org.color_slug);
  const description = stripRichText(org.description);

  const socials = SOCIAL_FIELDS
    .map((f) => ({ url: org[f.key] as string, label: f.label }))
    .filter((l) => Boolean(l.url));

  return (
    <li class="cb-dir-card">
      <a
        class={`cb-dir-logo${org.logo_url ? " cb-dir-logo--img" : ""}`}
        href={`${PROFILE_BASE}/i/${org.slug}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("dirProfileAria", { name: org.name })}
        style={{ background: org.logo_url ? undefined : color }}
      >
        {org.logo_url
          ? <img src={org.logo_url} alt="" loading="lazy" />
          : <span aria-hidden>{initials(org.name)}</span>}
      </a>

      <div class="cb-dir-namerow">
        <a
          class="cb-dir-name"
          href={`${PROFILE_BASE}/i/${org.slug}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {org.name}
        </a>
        <span class="cb-dir-region">{regionLabel(org, t)}</span>
      </div>

      {org.next_actions.length > 0 && (
        <div class="cb-dir-events">
          <div class="cb-dir-evhead">{t("dirEvHeading")}</div>
          {org.next_actions.map((a) => (
            <EventButton key={a.id} action={a} onOpen={onOpen} />
          ))}
        </div>
      )}

      <details class="cb-dir-more">
        <summary>
          <span class="cb-dir-caret" aria-hidden>›</span>{t("dirMore")}
        </summary>
        <div class="cb-dir-about">
          {description && (
            <div class="cb-dir-sec">
              <p class="cb-dir-sec-h">{t("dirWho")}</p>
              <p class="cb-dir-sec-p">{description}</p>
            </div>
          )}
          {org.meeting_schedule && (
            <div class="cb-dir-sec">
              <p class="cb-dir-sec-h">{t("dirWhen")}</p>
              <p class="cb-dir-sec-p">{org.meeting_schedule}</p>
            </div>
          )}
          {org.active_area && (
            <div class="cb-dir-sec">
              <p class="cb-dir-sec-h">{t("dirWhere")}</p>
              <p class="cb-dir-sec-p">{org.active_area}</p>
            </div>
          )}
          <a
            class="cb-dir-profile"
            href={`${PROFILE_BASE}/i/${org.slug}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("dirProfile")}
            <span class="cb-dir-profile-arrow" aria-hidden>↗</span>
          </a>
        </div>
      </details>

      {socials.length > 0 && (
        <div class="cb-dir-icons">
          {socials.map((l) => (
            <a
              key={l.label}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={l.label}
              title={l.url}
            >
              <svg viewBox={SOCIAL_ICON_VIEWBOX} width="18" height="18" fill="currentColor" aria-hidden="true">
                <path d={SOCIAL_ICONS[l.label] ?? ""} />
              </svg>
            </a>
          ))}
        </div>
      )}
    </li>
  );
};

const EventButton: FunctionComponent<{
  action: DirectoryNextAction;
  onOpen: (id: string) => void;
}> = ({ action, onOpen }) => {
  const t = useT();
  const locale = useLocale();
  const d = dateParts(action.start_at, locale);
  const label = action.title || action.meeting_point;
  return (
    <button
      type="button"
      class="cb-dir-ev"
      onClick={() => onOpen(action.id)}
      aria-label={t("detailsOpen", { title: label })}
    >
      <span class="cb-dir-ev-d">{d.weekday} {d.day}. {d.month}</span>
      <span class="cb-dir-ev-t">{label}</span>
      <span class="cb-dir-ev-go" aria-hidden>›</span>
    </button>
  );
};

// ─── Region filter (chips, mirrors BerlinWideEmbed) ──────────────────────────
const RegionFilter: FunctionComponent<{
  options: RegionOption[];
  selected: string | undefined;
  onSelect: (slug: string | undefined) => void;
  label: string;
}> = ({ options, selected, onSelect, label }) => {
  const t = useT();
  const labelId = useId();
  return (
    <div class="cb-region-filter-group">
      <span class="cb-region-filter-label" id={labelId}>{label}</span>
      <div class="cb-region-filter" role="group" aria-labelledby={labelId}>
        <button
          type="button"
          class={`cb-region-chip${!selected ? " active" : ""}`}
          onClick={() => onSelect(undefined)}
        >
          {t("filterAll")}
        </button>
        {options.map((o) => (
          <button
            key={o.slug}
            type="button"
            class={`cb-region-chip${selected === o.slug ? " active" : ""}`}
            onClick={() => onSelect(selected === o.slug ? undefined : o.slug)}
          >
            {o.name}
          </button>
        ))}
      </div>
    </div>
  );
};
